/**
 * ProviderMark — the rounded-square provider mark of REDESIGN §7a.
 *
 * OUR OWN simplified mark: a square in a colour that evokes the institution, carrying a
 * monogram we drew (`providers.ts`). No logo file is copied, referenced or fetched, and the
 * mark is never presented as the institution's official logo.
 *
 * Inline SVG, theme tokens only, no motion. Decorative by default (`aria-hidden`); pass `title`
 * — the provider's Hebrew name, which lives in each app's `i18n/he.ts` — wherever the mark is
 * the only thing naming the provider, and it becomes an `img` with an accessible name.
 */
import { PROVIDER_MONOGRAMS, providerMarkId } from './providers.ts';
import type { ProviderMarkSize } from './providers.ts';
import './kit.css';

export interface ProviderMarkProps {
  /** Financy `providerId`. Anything unknown falls back to the neutral mark. */
  providerId: string | null | undefined;
  /** 20 in dense rows, 28 in list rows, 40 in headers (§7a). */
  size?: ProviderMarkSize;
  /** Accessible name. Omit where an adjacent label already names the provider. */
  title?: string;
  className?: string;
}

export function ProviderMark({ providerId, size = 28, title, className }: ProviderMarkProps) {
  const id = providerMarkId(providerId);
  const labelled = typeof title === 'string' && title.length > 0;
  return (
    <svg
      className={`fk-mark fk-mark--${id}${className ? ` ${className}` : ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role={labelled ? 'img' : undefined}
      aria-hidden={labelled ? undefined : true}
      focusable="false"
    >
      {labelled ? <title>{title}</title> : null}
      <rect className="fk-mark__bg" width="24" height="24" rx="7" />
      <path className="fk-mark__ink" d={PROVIDER_MONOGRAMS[id]} />
    </svg>
  );
}
